import { lockScroll, unlockScroll } from '@theme/utilities';

/**
 * ナビゲーションのトリガー要素([data-category-overlay-trigger])から
 * カテゴリオーバーレイ切り替え用の共有タブバーをDOM上に動的に生成する。
 * Liquid側で section.blocks をループして生成しようとしたが、セクション本体
 * からループしても空扱いになりタブが描画されなかったため、既に存在する
 * トリガー要素を読み取って組み立てる方式にしている。
 *
 * 商品カテゴリとブランドは見た目の同じ2つのピルに分け、選択中のタブは
 * 個別ボタンの塗りではなく、ピルごとに1枚だけ置いたインジケーター(黒背景)を
 * transformでスライドさせて表現する。
 * @param {NodeListOf<HTMLElement>} triggers
 * @returns {HTMLElement | null}
 */
function buildTabsBar(triggers) {
  if (triggers.length === 0) return null;

  const bar = document.createElement('div');
  bar.className = 'ts-category-overlay-tabs';
  bar.id = 'ts-category-overlay-tabs';
  bar.setAttribute('role', 'tablist');

  const categoryGroup = document.createElement('div');
  categoryGroup.className = 'ts-category-overlay-tabs__group';

  const brandGroup = document.createElement('div');
  brandGroup.className = 'ts-category-overlay-tabs__group ts-category-overlay-tabs__group--brand';

  [categoryGroup, brandGroup].forEach((group) => {
    const indicator = document.createElement('span');
    indicator.className = 'ts-category-overlay-tabs__indicator';
    indicator.setAttribute('aria-hidden', 'true');
    group.appendChild(indicator);
  });

  triggers.forEach((trigger) => {
    const overlayId = trigger.dataset.categoryOverlayTrigger ?? '';
    if (!overlayId) return;

    const labelEn = trigger.querySelector('.ts-nav-item__en')?.textContent ?? '';
    const labelJa = trigger.querySelector('.ts-nav-item__ja')?.textContent ?? '';
    const isBrand = trigger.closest('.ts-nav-item--brand') !== null;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'ts-category-overlay-tabs__tab';
    button.dataset.categoryTabTarget = overlayId;

    const en = document.createElement('span');
    en.className = 'ts-category-overlay-tabs__tab-en';
    en.textContent = labelEn;

    const ja = document.createElement('span');
    ja.className = 'ts-category-overlay-tabs__tab-ja';
    ja.textContent = labelJa;

    button.append(en, ja);
    (isBrand ? brandGroup : categoryGroup).appendChild(button);
  });

  if (categoryGroup.children.length > 1) bar.appendChild(categoryGroup);
  if (brandGroup.children.length > 1) bar.appendChild(brandGroup);

  document.body.appendChild(bar);
  return bar;
}

/** @type {NodeListOf<HTMLElement>} */
const triggers = document.querySelectorAll('[data-category-overlay-trigger]');
const tabsBar = buildTabsBar(triggers);

/*
  THIRD Space: これまでdocument.body.style.overflow = 'hidden'で
  スクロールを止めていたが、position: fixed + overflow-x: autoの
  タブバー(.ts-category-overlay-tabs、幅が画面に収まらない時は横スクロール)
  がbodyではなくhtml要素側のスクロール可能領域(scrollWidth)を広げてしまい、
  bodyのoverflowを止めても横に少しはみ出せてしまう不具合があった。
  他の標準コンポーネント(assets/utilities.jsのlockScroll/unlockScroll)と
  同じ、html要素に scroll-lock 属性を付ける方式に統一して、html側も
  含めて確実にロックする。ownerはタブバー(常時DOMに存在し続ける要素)を使う。
*/
const scrollLockOwner = tabsBar ?? document.body;

/**
 * アクティブなタブの位置までピルごとのインジケーターをスライドさせる。
 * そのピル内にアクティブなタブがなければインジケーターを非表示にする。
 * @param {boolean} [skipTransition] - 初回表示など、アニメーションなしで瞬時に合わせたい場合はtrue
 */
function updateIndicators(skipTransition) {
  if (!tabsBar) return;

  /** @type {NodeListOf<HTMLElement>} */
  (tabsBar.querySelectorAll('.ts-category-overlay-tabs__group')).forEach((group) => {
    const indicator = /** @type {HTMLElement | null} */ (group.querySelector('.ts-category-overlay-tabs__indicator'));
    const activeTab = /** @type {HTMLElement | null} */ (group.querySelector('.ts-category-overlay-tabs__tab.active'));
    if (!indicator) return;

    if (skipTransition) indicator.classList.add('ts-category-overlay-tabs__indicator--no-transition');

    if (activeTab) {
      // activeTab.offsetLeft はoffsetParent(position:relativeのgroup)基準の
      // 座標で、インジケーターの静止位置(CSSのleft: 6px、groupのpaddingぶん)と
      // 同じ基準。差分だけをtranslateXに渡せばよい。
      indicator.style.width = `${activeTab.offsetWidth}px`;
      indicator.style.transform = `translateX(${activeTab.offsetLeft - 6}px)`;
      indicator.classList.add('is-active');
    } else {
      indicator.classList.remove('is-active');
    }

    if (skipTransition) {
      void indicator.offsetWidth;
      indicator.classList.remove('ts-category-overlay-tabs__indicator--no-transition');
    }
  });
}

/**
 * @param {string} overlayId
 * @param {boolean} [skipTransition]
 */
function setActiveTab(overlayId, skipTransition) {
  if (!tabsBar) return;
  /** @type {NodeListOf<HTMLElement>} */
  (tabsBar.querySelectorAll('[data-category-tab-target]')).forEach((tabButton) => {
    tabButton.classList.toggle('active', tabButton.dataset.categoryTabTarget === overlayId);
  });
  updateIndicators(skipTransition);
}

/**
 * @param {HTMLElement} overlay
 */
function closeOverlay(overlay) {
  overlay.classList.remove('open');
  unlockScroll(scrollLockOwner);
  if (tabsBar) tabsBar.classList.remove('open');
}

/**
 * 閉じた状態から開く場合(通常のフェードイン)。
 * @param {HTMLElement} overlay
 */
function openOverlay(overlay) {
  overlay.classList.add('open');
  lockScroll(scrollLockOwner);
  if (tabsBar) tabsBar.classList.add('open');
  setActiveTab(overlay.id, true);
}

/**
 * タブクリックでの切り替え専用。旧オーバーレイのフェードアウトと新オーバーレイの
 * フェードインが同時に走ると背後のページが一瞬透けて見えるため、切り替え中だけ
 * transitionを止めて瞬時に入れ替える(インジケーターは逆にアニメーションさせたい
 * ので、こちらはskipTransitionを渡さない)。
 * @param {HTMLElement} overlay
 */
function switchOverlay(overlay) {
  const current = /** @type {HTMLElement | null} */ (document.querySelector('.ts-category-overlay.open'));
  if (current === overlay) return;
  if (!current) {
    openOverlay(overlay);
    return;
  }

  current.classList.add('ts-category-overlay--no-transition');
  overlay.classList.add('ts-category-overlay--no-transition');
  current.classList.remove('open');
  overlay.classList.add('open');
  setActiveTab(overlay.id);

  void overlay.offsetHeight;
  requestAnimationFrame(() => {
    current.classList.remove('ts-category-overlay--no-transition');
    overlay.classList.remove('ts-category-overlay--no-transition');
  });
}

triggers.forEach((trigger) => {
  const overlayId = trigger.dataset.categoryOverlayTrigger ?? '';
  const overlay = document.getElementById(overlayId);
  if (!overlay) return;

  trigger.addEventListener('click', (event) => {
    event.preventDefault();
    openOverlay(overlay);
  });
});

/*
  モバイルの三本線メニューは、以前は開くとカテゴリ名だけの縦並びリスト
  (sections/site-header.liquidの.ts-site-header__nav、<details>要素で
  開閉)を表示し、そこから項目をタップして初めてオーバーレイに切り替わる
  2段階の操作だった。1段階目のリストを経由せず、三本線タップで最初の
  カテゴリ(先頭のtrigger、通常はApparel)のオーバーレイへ直接遷移させる。
*/
const mobileToggle = document.querySelector('.ts-site-header__mobile-toggle');
const mobileToggleSummary = mobileToggle?.querySelector('summary');
if (mobileToggleSummary && triggers.length > 0) {
  const firstOverlayId = triggers[0]?.dataset.categoryOverlayTrigger ?? '';
  const firstOverlay = document.getElementById(firstOverlayId);
  if (firstOverlay) {
    mobileToggleSummary.addEventListener('click', (event) => {
      event.preventDefault();
      openOverlay(firstOverlay);
    });
  }
}

document.querySelectorAll('.ts-category-overlay').forEach((overlayEl) => {
  const overlay = /** @type {HTMLElement} */ (overlayEl);

  const closeButton = overlay.querySelector('[data-overlay-close]');
  if (closeButton) {
    closeButton.addEventListener('click', () => closeOverlay(overlay));
  }

  overlay.addEventListener('click', (event) => {
    if (event.target === overlay) closeOverlay(overlay);
  });
});

if (tabsBar) {
  /** @type {NodeListOf<HTMLElement>} */
  (tabsBar.querySelectorAll('[data-category-tab-target]')).forEach((tabButton) => {
    tabButton.addEventListener('click', () => {
      const targetId = tabButton.dataset.categoryTabTarget ?? '';
      const target = document.getElementById(targetId);
      if (!target) return;
      switchOverlay(target);
    });
  });

  window.addEventListener('resize', () => updateIndicators(true));
}

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  /** @type {NodeListOf<HTMLElement>} */
  (document.querySelectorAll('.ts-category-overlay.open')).forEach(closeOverlay);
});
